import * as Sentry from '@sentry/react';
import { getDefaultStore } from 'jotai';
import { clerkServiceErrorVisibleAtom } from '../stores/clerk-service-error.store';

const CLERK_SDK_MARKERS = ['ClerkJS:', 'not supported yet'] as const;
const CLERK_NETWORK_ERROR_MARKER = 'ClerkJS: Network error';

export function looksLikeClerkSdkBreakage(reason: unknown): reason is Error {
  if (!(reason instanceof Error)) {
    return false;
  }
  const message = reason.message ?? '';
  return CLERK_SDK_MARKERS.some((marker) => message.includes(marker));
}

// ClerkJS wraps its own internal fetch failures (flaky/absent mobile network,
// e.g. a session "touch" call) with this exact prefix — not an SDK/server
// contract mismatch. Not actionable; must not surface as a breakage banner.
export function isClerkNetworkError(reason: unknown): reason is Error {
  if (!(reason instanceof Error)) {
    return false;
  }
  return (reason.message ?? '').includes(CLERK_NETWORK_ERROR_MARKER);
}

export interface ClerkErrorHandlerState {
  reportedThisSession: boolean;
}

export function createClerkErrorHandlerState(): ClerkErrorHandlerState {
  return { reportedThisSession: false };
}

export interface ClerkErrorHandlerDeps {
  state?: ClerkErrorHandlerState;
  setVisible?: (visible: boolean) => void;
  isVisible?: () => boolean;
  captureException?: typeof Sentry.captureException;
}

const moduleState = createClerkErrorHandlerState();

export function handleClerkError(error: Error, deps: ClerkErrorHandlerDeps = {}): void {
  const state = deps.state ?? moduleState;
  const store = getDefaultStore();
  const setVisible =
    deps.setVisible ?? ((value: boolean) => store.set(clerkServiceErrorVisibleAtom, value));
  const isVisible = deps.isVisible ?? (() => store.get(clerkServiceErrorVisibleAtom));
  const captureException = deps.captureException ?? Sentry.captureException;

  if (!isVisible()) {
    setVisible(true);
  }

  // Rate-limit Sentry to one event per session: the same SDK/server mismatch
  // typically fires on every retry while the user is stuck, and we don't want
  // to flood the project quota.
  if (!state.reportedThisSession) {
    state.reportedThisSession = true;
    captureException(error, {
      level: 'error',
      tags: { source: 'clerkjs', kind: 'sdk-version-mismatch' },
      fingerprint: ['clerkjs-sdk-response-unsupported'],
    });
  }
}

export function installClerkErrorHandler(): () => void {
  const onUnhandled = (event: PromiseRejectionEvent) => {
    if (isClerkNetworkError(event.reason)) {
      // Not actionable (flaky/absent network) and not an SDK mismatch —
      // suppress the default logging but skip the breakage banner/capture.
      event.preventDefault();
      return;
    }
    if (looksLikeClerkSdkBreakage(event.reason)) {
      // Suppress the default unhandled-rejection logging and Sentry's
      // automatic global capture — we've already reported it explicitly with
      // tags above, so letting it bubble would create a duplicate event.
      event.preventDefault();
      handleClerkError(event.reason);
    }
  };
  const onError = (event: ErrorEvent) => {
    if (isClerkNetworkError(event.error)) {
      event.preventDefault();
      return;
    }
    if (looksLikeClerkSdkBreakage(event.error)) {
      event.preventDefault();
      handleClerkError(event.error);
    }
  };
  window.addEventListener('unhandledrejection', onUnhandled);
  window.addEventListener('error', onError);
  return () => {
    window.removeEventListener('unhandledrejection', onUnhandled);
    window.removeEventListener('error', onError);
  };
}
